import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '../../icons';
import {
  Close as CloseIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
  History as HistoryIcon,
  FactCheck as FactCheckIcon,
  Build as BuildIcon,
  Description as DescriptionIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';

// --- Demonstration Data Injection ---
// To be removed once real DB data is available for all fields.
const DEMO_MACHINE_DATA = {
  21: { // 자동 포장기(2라인)
    risk_stats: { high: 3, mid: 2, low: 1 },
    measure_stats: { none: 0, set: 6, done: 5 },
    accident_history: { accident: 1, near_miss: 1 },
    maintenance: { broken: 0, compliance: 0 } // placeholders if not specified
  },
  23: { // 반죽,투입,분할,몰딩(2라인)
    risk_stats: { high: 1, mid: 1, low: 0 },
    measure_stats: { none: 0, set: 2, done: 2 },
    accident_history: { accident: 0, near_miss: 0 },
    maintenance: { broken: 1, compliance: 100 }
  },
  20: { // 수평믹서2
    risk_stats: { high: 2, mid: 1, low: 0 },
    measure_stats: { none: 0, set: 3, done: 3 },
    accident_history: { accident: 0, near_miss: 0 },
    maintenance: { broken: 3, compliance: 98 }
  }
};

const StatusBadge = ({ status, onStatusClick }) => {
  const statusList = [
    { id: 1, label: '가동중', color: 'emerald', originalIds: [1, 2, 3, 4, 5, 6, 10] },
    { id: 2, label: '정비중', color: 'orange', originalIds: [8, 11] },
    { id: 3, label: '운휴', color: 'red', originalIds: [9] }
  ];

  // Find which category the current status belongs to
  const mapping = statusList.find(s => s.originalIds.includes(status));
  const normalizedStatus = mapping ? mapping.id : 3; // Default to '운휴' if not found

  const isViolation = status === 10 || status === 11;
  const isMaintenanceViolation = status === 11;

  return (
    <div className="flex gap-2">
      {statusList.map((item) => {
        const isActive = normalizedStatus === item.id;

        const colorStyles = {
          emerald: { active: 'text-emerald-400 bg-emerald-500/20 border-emerald-500/30', glow: '0 0 15px rgba(16,185,129,0.3)', dot: 'bg-emerald-500' },
          orange: { active: 'text-orange-400 bg-orange-500/20 border-orange-500/30', glow: '0 0 15px rgba(245,158,11,0.3)', dot: 'bg-orange-500' },
          red: { active: 'text-red-400 bg-red-500/20 border-red-500/30', glow: '0 0 15px rgba(239,68,68,0.3)', dot: 'bg-red-500' }
        };

        const currentStyle = colorStyles[item.color];

        return (
          <div
            key={item.id}
            onClick={() => {
              if (isActive && isMaintenanceViolation && onStatusClick) {
                onStatusClick();
              }
            }}
            className={`
              flex items-center gap-1.5 px-2.5 py-1 rounded-lg border transition-all duration-500
              ${isActive
                ? `${currentStyle.active} opacity-100 scale-100 ${isMaintenanceViolation ? 'cursor-pointer hover:brightness-125' : ''}`
                : 'bg-white/5 text-slate-600 border-white/5 opacity-30 scale-95 grayscale'}
            `}
            style={{
              boxShadow: isActive ? currentStyle.glow : 'none',
              backdropFilter: isActive ? 'blur(4px)' : 'none'
            }}
          >
            <div className={`w-1.5 h-1.5 rounded-full ${isActive ? currentStyle.dot : 'bg-slate-700'}`} />
            <span className="text-[10px] font-black tracking-tighter uppercase whitespace-nowrap flex items-center gap-1">
              {item.label}
              {isActive && isViolation && (
                <WarningIcon sx={{ fontSize: 12, color: 'inherit' }} />
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const Section = ({ icon: Icon, title, children }) => (
  <div className="mb-8 last:mb-0">
    <div className="flex items-center gap-2.5 mb-2 px-1">
      <div className="flex p-2 rounded-xl bg-indigo-500/10 text-indigo-400 shadow-inner">
        <Icon sx={{ fontSize: 20 }} />
      </div>
      <h3 className="text-[17px] font-black text-slate-100 tracking-tight m-0 uppercase opacity-90">{title}</h3>
    </div>
    <div
      className="bg-slate-800/20 rounded-2xl px-4 py-4 backdrop-blur-md"
      style={{ border: '1px solid rgba(255,255,255,0.06)', boxShadow: 'inset 0 0 20px rgba(0,0,0,0.2)' }}
    >
      {children}
    </div>
  </div>
);

const InfoItem = ({ label, value }) => (
  <div
    className="flex justify-between items-center py-1.5 first:pt-0"
    style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}
  >
    <span className="text-[12px] text-slate-400 font-bold">{label}</span>
    <span className="text-[13px] text-slate-100 font-bold">{value || '-'}</span>
  </div>
);

const MetricCard = ({ label, value, unit, colorClass, onClick, bgColor = 'bg-white', borderColor = 'rgba(255,255,255,0.06)', showDot = false }) => (
  <div
    onClick={onClick}
    className={`flex flex-col items-center justify-center p-3 rounded-2xl ${bgColor}/5 hover:${bgColor}/10 hover:brightness-125 transition-all cursor-pointer group`}
    style={{ border: `1px solid ${borderColor}` }}
  >
    <div className="flex items-center gap-1.5 mb-2">
      {showDot && <div className={`w-1.5 h-1.5 rounded-full ${colorClass.replace('text-', 'bg-')}`} />}
      <span className="text-[12px] text-slate-500 block font-bold">{label}</span>
    </div>
    <div className="flex items-baseline gap-0.5">
      <span className={`text-2xl font-black ${colorClass}`}>{value}</span>
      <span className="text-xs text-slate-500 font-bold">{unit}</span>
    </div>
  </div>
);

const SubHeader = ({ title, onToggle, isExpanded }) => (
  <div className="flex justify-between items-center mb-3 px-1">
    <span className="text-[12px] text-slate-400 font-bold">{title}</span>
    <button
      onClick={onToggle}
      className={`text-[10px] font-black transition-all uppercase tracking-tight ${isExpanded ? 'text-orange-400 hover:text-orange-300' : 'text-indigo-400 hover:text-indigo-300'}`}
    >
      {isExpanded ? '닫기' : '상세보기'}
    </button>
  </div>
);

export default function MachineDetailInfo({ isOpen, onClose, data, onOpenHistory, onStatusClick, onDownloadCert }) {
  const scrollRef = useRef(null);
  const [showScrollDown, setShowScrollDown] = useState(true);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      const isBottom = scrollHeight - scrollTop <= clientHeight + 20;
      setShowScrollDown(!isBottom);
    }
  };

  useEffect(() => {
    checkScroll();
  }, [isOpen, data]);



  const mchData = data || {};
  // Ensure we check all possible ID fields and convert to string for consistent lookup in DEMO_MACHINE_DATA
  const currentMchId = String(mchData.mchId || mchData.machine_id || mchData.id || '');
  const demoData = DEMO_MACHINE_DATA[currentMchId];

  const handleScrollAction = () => {
    if (scrollRef.current) {
      if (showScrollDown) {
        scrollRef.current.scrollTo({ top: scrollRef.current.scrollTop + 350, behavior: 'smooth' });
      } else {
        scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const openPopup = (type) => {
    let config = { title: '', columns: [], data: [] };

    if (type === 'riskStatus') {
      config = {
        title: '위험성 평가 현황 세부 이력',
        columns: [
          { header: '코드', accessorKey: 'risk_factor_code' },
          {
            header: '위험 레벨', accessorKey: 'risk_danger_level', cell: ({ getValue }) => (
              <span className={`font-black ${getValue() >= 3 ? 'text-red-400' : 'text-orange-400'}`}>LEVEL {getValue()}</span>
            )
          },
          { header: '위험 상황', accessorKey: 'risk_situation' },
          { header: '발생 원인', accessorKey: 'risk_cause' },
        ],
        data: mchData.riskData || []
      };
    } else if (type === 'riskMeasure') {
      config = {
        title: '개선대책 수립 현황 세부 이력',
        columns: [
          { header: '코드', accessorKey: 'risk_factor_code' },
          { header: '위험 레벨', accessorKey: 'risk_danger_level' },
          { header: '위험 상황', accessorKey: 'risk_situation' },
          { header: '개선 대책', accessorKey: 'risk_measure' },
        ],
        data: mchData.riskData || []
      };
    } else if (type === 'accident') {
      config = {
        title: '설비 사고 및 아차사고 이력',
        columns: [
          { header: '발생 일자', accessorKey: 'broken_date' },
          { header: '사고 내용', accessorKey: 'broken_description' },
          { header: '조치 내용', accessorKey: 'maintenance_description' },
        ],
        data: mchData.brokenLogData || []
      };
    } else if (type === 'maintenance') {
      config = {
        title: '설비 보전 및 점검 이력',
        columns: [
          { header: '일자', accessorKey: 'date' },
          { header: '구분', accessorKey: 'type' },
          { header: '상세 내용', accessorKey: 'description' },
        ],
        data: [
          { date: '2025-12-01', type: '정기점검', description: '메인 모터 베어링 교체 및 윤활유 보충' },
          { date: '2025-11-15', type: '수시점검', description: '컨베이어 벨트 장력 조절' },
          { date: '2025-10-10', type: '정기점검', description: '전기 패널 먼지 제거 및 단자 체결 확인' },
        ]
      };
    }

    if (onOpenHistory) {
      onOpenHistory(config);
    }
  };

  return (
    <div
      id="machine-detail-info"
      className={`
      absolute top-6 right-4 bottom-6 w-[440px] z-50
      bg-[#0a0f1e]/80 backdrop-blur-3xl rounded-xl
      shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)]
      flex flex-col
      transition-[transform,opacity] duration-700 ease-[cubic-bezier(0.2,1,0.2,1)] will-change-transform
      ${isOpen ? 'translate-x-0 opacity-100' : 'translate-x-[480px] opacity-0 pointer-events-none'}
    `}
      style={{ border: '1px solid rgba(255,255,255,0.12)' }}
    >
      {/* Premium Glow backgrounds */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 blur-[80px] pointer-events-none rounded-full" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-500/5 blur-[80px] pointer-events-none rounded-full" />

      {/* Header */}
      <div className="p-8 pb-4 flex justify-between items-start z-10">
        <div>
          <h2 className="text-2xl font-black text-white tracking-tighter leading-tight mb-3">
            {mchData.machine_name || '설비명 미등록'}
          </h2>
          <StatusBadge status={mchData.machine_status || 0} onStatusClick={onStatusClick} />
        </div>
        <button
          onClick={onClose}
          className="p-2.5 rounded-2xl bg-white/5 text-slate-400 hover:text-white hover:bg-white/10 transition-all active:scale-90"
          style={{ border: '1px solid rgba(255,255,255,0.05)' }}
        >
          <CloseIcon sx={{ fontSize: 24 }} />
        </button>
      </div>

      {/* Content */}
      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex-1 overflow-y-auto px-8 pb-4 z-10
          [&::-webkit-scrollbar]:w-1 
          [&::-webkit-scrollbar-thumb]:bg-white/10 
          [&::-webkit-scrollbar-thumb]:rounded-full 
          [&::-webkit-scrollbar-track]:bg-transparent"
      >
        <Section icon={SettingsIcon} title="설비 상세 정보">
          <div className="space-y-0.5">
            <InfoItem label="관리 번호" value={mchData.machine_manage_code} />
            <InfoItem label="카테고리" value={mchData.machine_category_name} />
            <InfoItem label="장비 그룹" value={mchData.machine_group_name} />
            <InfoItem label="제조사 / 모델" value={`${mchData.machine_manufacturer || '-'} / ${mchData.machine_model_name || '-'}`} />
            <InfoItem label="규격 / 전압" value={`${mchData.machine_size || '-'} / ${mchData.machine_volt || '-'}`} />
            <InfoItem label="구입 일자" value={mchData.machine_purchase_date} />
            <InfoItem label="최종 업데이트" value={mchData.machine_update_date} />
          </div>
        </Section>

        <Section icon={WarningIcon} title="위험성 평가 정보">
          <div className="space-y-6">
            <div>
              <SubHeader
                title="위험성 평가 현황"
                onToggle={() => openPopup('riskStatus')}
              />
              <div className="grid grid-cols-3 gap-2 px-1 mb-4">
                <MetricCard
                  label="상"
                  value={demoData ? demoData.risk_stats.high : (mchData.riskData?.filter(r => r.risk_danger_level >= 3).length || 0)}
                  unit="건"
                  colorClass="text-red-400"
                  bgColor="bg-red-500"
                  borderColor="rgba(239,68,68,0.1)"
                  showDot={true}
                />
                <MetricCard
                  label="중"
                  value={demoData ? demoData.risk_stats.mid : (mchData.riskData?.filter(r => r.risk_danger_level === 2).length || 0)}
                  unit="건"
                  colorClass="text-orange-400"
                  bgColor="bg-orange-500"
                  borderColor="rgba(245,158,11,0.1)"
                  showDot={true}
                />
                <MetricCard
                  label="하"
                  value={demoData ? demoData.risk_stats.low : (mchData.riskData?.filter(r => r.risk_danger_level <= 1).length || 0)}
                  unit="건"
                  colorClass="text-emerald-400"
                  bgColor="bg-emerald-500"
                  borderColor="rgba(16,185,129,0.1)"
                  showDot={true}
                />
              </div>
            </div>

            <div>
              <SubHeader
                title="개선대책 현황"
                onToggle={() => openPopup('riskMeasure')}
              />
              <div className="grid grid-cols-3 gap-2 px-1 mb-0">
                <MetricCard
                  label="미수립"
                  value={demoData ? demoData.measure_stats.none : (mchData.riskData?.filter(r => !r.risk_measure || r.risk_measure.includes('필요')).length || 0)}
                  unit="건"
                  colorClass="text-slate-400"
                  bgColor="bg-slate-500"
                  borderColor="rgba(148,163,184,0.1)"
                />
                <MetricCard
                  label="수립"
                  value={demoData ? demoData.measure_stats.set : 0}
                  unit="건"
                  colorClass="text-indigo-400"
                  bgColor="bg-indigo-500"
                  borderColor="rgba(99,102,241,0.1)"
                />
                <MetricCard
                  label="완료"
                  value={demoData ? demoData.measure_stats.done : (mchData.riskData?.filter(r => r.risk_measure && !r.risk_measure.includes('필요')).length || 0)}
                  unit="건"
                  colorClass="text-emerald-400"
                  bgColor="bg-emerald-500"
                  borderColor="rgba(16,185,129,0.1)"
                />
              </div>
            </div>
          </div>
        </Section>

        <Section icon={HistoryIcon} title="설비 사고 이력">
          <div>
            <SubHeader
              title="사고 및 아차사고 현황"
              onToggle={() => openPopup('accident')}
            />
            <div className="grid grid-cols-2 gap-3 px-1">
              <MetricCard
                label="사고건수"
                value={demoData ? demoData.accident_history.accident : (mchData.brokenLogData?.length || 0)}
                unit="건"
                colorClass="text-red-400"
                bgColor="bg-red-500"
                borderColor="rgba(239,68,68,0.1)"
              />
              <MetricCard
                label="아차사고건수"
                value={demoData ? demoData.accident_history.near_miss : 0}
                unit="건"
                colorClass="text-orange-400"
                bgColor="bg-orange-500"
                borderColor="rgba(245,158,11,0.1)"
              />
            </div>
          </div>
        </Section>

        <Section icon={FactCheckIcon} title="안전 점검 지표">
          <div className="space-y-4">
            <InfoItem label="최종 점검일" value={new Date().toLocaleDateString()} />

            <div className="grid grid-cols-2 gap-3 mb-4">
              <MetricCard
                label="점검 준수율"
                value="98"
                unit="%"
                colorClass="text-indigo-400"
                bgColor="bg-indigo-500"
                borderColor="rgba(99,102,241,0.1)"
              />
              <MetricCard
                label="최종 점검 상태"
                value="PASS"
                unit=""
                colorClass="text-emerald-400"
                bgColor="bg-emerald-500"
                borderColor="rgba(16,185,129,0.1)"
              />
            </div>

            <div
              className={`flex items-center gap-4 p-4 rounded-2xl bg-white/5 hover:bg-white/10 transition-all ${onDownloadCert ? 'cursor-pointer active:scale-95' : 'cursor-default'} group`}
              style={{ border: '1px solid rgba(255,255,255,0.05)' }}
              onClick={() => onDownloadCert && onDownloadCert(mchData.machine_manage_code)}
            >
              <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center text-indigo-400 group-hover:bg-indigo-500/20 transition-colors">
                <PdfIcon sx={{ fontSize: 24 }} />
              </div>
              <div className="flex-1">
                <div className="text-[13px] text-slate-200 font-black leading-tight mb-1 uppercase tracking-tight">자율안전확인 증명서</div>
                <div className="text-[10px] text-slate-500 font-mono tracking-widest uppercase">PDF</div>
              </div>
            </div>
          </div>
        </Section>

        <Section icon={BuildIcon} title="설비 보전">
          <div className="space-y-4">
            <SubHeader
              title="설비 이력"
              onToggle={() => openPopup('maintenance')}
            />
            <div className="grid grid-cols-2 gap-3">
              <MetricCard
                label="고장건수"
                value={demoData ? demoData.maintenance.broken : "2"}
                unit="건"
                colorClass="text-red-400"
                bgColor="bg-red-500"
                borderColor="rgba(239,68,68,0.1)"
              />
              <MetricCard
                label="예방보전실행율"
                value={demoData ? demoData.maintenance.compliance : "100"}
                unit="%"
                colorClass="text-emerald-400"
                bgColor="bg-emerald-500"
                borderColor="rgba(16,185,129,0.1)"
              />
            </div>
            <div className="flex items-center justify-between px-1">
              <span className="text-[12px] text-slate-400 font-medium">다음 보전 예정일</span>
              <div className="flex items-center gap-2">
                <span className="text-[13px] text-orange-400 font-black tracking-tight">2026-02-15</span>
                <span className="px-1.5 py-0.5 rounded bg-orange-500/10 text-[9px] text-orange-300 font-black">D-38</span>
              </div>
            </div>
            <div className="relative h-2 bg-white/5 rounded-full overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-orange-400 w-[72%] shadow-[0_0_10px_rgba(245,158,11,0.4)]" />
            </div>
          </div>
        </Section>

        <Section icon={DescriptionIcon} title="MSDS">
          <div className="space-y-4">
            <div className="space-y-0.5">
              <InfoItem label="잉크청소" value='아세톤' />
              <InfoItem label="자동날인" value='이소프로필 알코올' />
            </div>
            <div
              className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 hover:bg-white/10 transition-all cursor-pointer group active:scale-95"
              style={{ border: '1px solid rgba(255,255,255,0.05)' }}
            >
              <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center text-red-500 group-hover:bg-red-500/20 transition-colors">
                <DescriptionIcon sx={{ fontSize: 24 }} />
              </div>
              <div className="flex-1">
                <div className="text-[13px] text-slate-200 font-black leading-tight mb-1">Material Safety Data Sheet</div>
                <div className="text-[10px] text-slate-500 font-mono tracking-widest uppercase">PDF • 2.44 MB • VER 1.2</div>
              </div>
            </div>
          </div>
        </Section>

        {/* Padding for scroll button */}
        <div className="h-8" />
      </div>

      {/* Floating Scroll Indicator / Back to Top */}
      <button
        onClick={handleScrollAction}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-slate-900/40 backdrop-blur-md text-slate-400 shadow-[0_8px_20px_rgba(0,0,0,0.4)] flex items-center justify-center hover:bg-slate-800/60 hover:text-white hover:scale-110 active:scale-90 transition-all z-20"
        style={{ border: '1px solid rgba(255,255,255,0.08)' }}
      >
        {showScrollDown ? <ArrowDownIcon /> : <ArrowUpIcon />}
      </button>
    </div>
  );
}
